// Property list sorting — field + direction, extracted from Dashboard so the
// comparator is unit-testable on its own (the component that used to hold it
// inline is ~1,300 lines and can't be rendered cheaply in a test).
//
// The UI splits the old flat "score_desc / assessed_desc / …" list into a FIELD
// choice plus a direction toggle: 6 fields + one ↑/↓ arrow instead of 11 rows.
// Missing values always sink to the bottom, in BOTH directions — sorting
// ascending should not float rows that have no value to the top.

import { Property } from "../types";
import { computeDealScore } from "./dealScore";
import { statusMeta } from "./statusMeta";
import { resolveAmountOwed } from "./dataQuality";

export type SortField =
  | "score"
  | "assessed"
  | "amount_due"
  | "equity"
  | "equity_pct"
  | "owed_ratio"
  | "per_acre"
  | "auction"
  | "date_added";

export interface SortFieldOption {
  value: SortField;
  label: string;
  // directional=false → only one order makes sense (best deal score first), so
  // the direction toggle is hidden for it.
  directional: boolean;
}

export const SORT_FIELDS: SortFieldOption[] = [
  { value: "score", label: "Best deal score", directional: false },
  { value: "assessed", label: "Assessed value", directional: true },
  { value: "amount_due", label: "Amount due", directional: true },
  { value: "equity", label: "Est. equity", directional: true },
  { value: "equity_pct", label: "Equity % of value", directional: true },
  { value: "owed_ratio", label: "Owed ÷ value", directional: true },
  { value: "per_acre", label: "Value per acre", directional: true },
  { value: "auction", label: "Auction date", directional: true },
  { value: "date_added", label: "Date added", directional: true },
];

// `primary` is the default (non-reversed) direction; `reverse` is what one click
// of the arrow flips to. Used for the toggle's tooltip / aria-label.
export const SORT_DIR_LABELS: Record<SortField, { primary: string; reverse: string }> = {
  score: { primary: "Best first", reverse: "Best first" },
  assessed: { primary: "Highest first", reverse: "Lowest first" },
  amount_due: { primary: "Highest first", reverse: "Lowest first" },
  equity: { primary: "Highest first", reverse: "Lowest first" },
  equity_pct: { primary: "Highest first", reverse: "Lowest first" },
  owed_ratio: { primary: "Lowest first", reverse: "Highest first" },
  per_acre: { primary: "Lowest first", reverse: "Highest first" },
  auction: { primary: "Soonest first", reverse: "Latest first" },
  date_added: { primary: "Newest first", reverse: "Oldest first" },
};

export const DEFAULT_SORT_FIELD: SortField = "score";

export function sortDirTooltip(field: SortField, reversed: boolean): string {
  const l = SORT_DIR_LABELS[field];
  return reversed
    ? `${l.reverse} — click for ${l.primary.toLowerCase()}`
    : `${l.primary} — click for ${l.reverse.toLowerCase()}`;
}

// Numeric value for a field, or null when the property has none (0 amount-due
// is an "unknown" sentinel, not a real zero — see project memory — so it counts
// as missing and sinks rather than sorting as the smallest number).
function numericValue(field: SortField, p: Property): number | null {
  switch (field) {
    case "assessed":
      return p.assessedValue > 0 ? p.assessedValue : null;
    case "amount_due":
      return p.amountDue > 0 ? p.amountDue : null;
    case "equity": {
      if (!(p.assessedValue > 0)) return null;
      const owed = p.amountDue > 0 ? p.amountDue : 0;
      return p.assessedValue - owed;
    }
    // Ratios use the debt figure the panel shows (owedOf) and need a real
    // assessed value; a row missing an input is "no value" and sinks.
    case "equity_pct": {
      const owed = owedOf(p);
      if (!(p.assessedValue > 0) || owed == null) return null;
      return (p.assessedValue - owed) / p.assessedValue;
    }
    case "owed_ratio": {
      const owed = owedOf(p);
      if (!(p.assessedValue > 0) || owed == null) return null;
      return owed / p.assessedValue;
    }
    case "per_acre": {
      const acres = p.building?.lotSizeAcres;
      if (!(p.assessedValue > 0) || !(acres != null && acres > 0)) return null;
      return p.assessedValue / acres;
    }
    default:
      return null;
  }
}

// The debt figure the panel shows (published amount, opening bid, then the
// original loan), or null when none is published.
function owedOf(p: Property): number | null {
  const owed = resolveAmountOwed(p);
  return owed.state === "unavailable" || !(owed.amount != null && owed.amount > 0) ? null : owed.amount;
}

const NUMERIC_FIELDS: readonly SortField[] = [
  "assessed",
  "amount_due",
  "equity",
  "equity_pct",
  "owed_ratio",
  "per_acre",
];
// Ratios where the smallest value is the best deal, so the default order is ascending.
const LOW_FIRST_FIELDS: readonly SortField[] = ["owed_ratio", "per_acre"];

function isNumericField(field: SortField): boolean {
  return NUMERIC_FIELDS.includes(field);
}

function dateValue(field: SortField, p: Property): number | null {
  const raw = field === "auction" ? p.auctionDate : field === "date_added" ? p.createdAt : null;
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? null : t;
}

// Deal scores are cached per row object. The list re-sorts on every filter,
// watch or hide change, and recomputing the score of every row in a county
// each time was the sort's main cost. Rows are replaced, never mutated, on a
// refetch, so a row object's score only changes with the clock: the score's
// one time input is the whole number of days until the auction
// (dealScore urgencyRaw), so an entry stays valid while that number is the same.
const DAY_MS = 24 * 60 * 60 * 1000;

interface CachedScore {
  auctionMs: number | null;
  days: number | null;
  score: number;
}

const scoreCache = new WeakMap<Property, CachedScore>();

function daysUntil(auctionMs: number | null, now: number): number | null {
  return auctionMs === null ? null : Math.floor((auctionMs - now) / DAY_MS);
}

/** Deal score used for sorting (terminal rows score -1 so they sink). */
export function sortScore(p: Property, now: number = Date.now()): number {
  const cached = scoreCache.get(p);
  if (cached && daysUntil(cached.auctionMs, now) === cached.days) return cached.score;
  const t = p.auctionDate ? new Date(p.auctionDate).getTime() : NaN;
  const auctionMs = Number.isNaN(t) ? null : t;
  const score = statusMeta(p.status).isTerminal ? -1 : computeDealScore(p).score;
  scoreCache.set(p, { auctionMs, days: daysUntil(auctionMs, now), score });
  return score;
}

// Precomputed deal scores. Only built for the score field. Exported so the
// caller can memoize it if it wants.
export function scoreMap(properties: Property[]): Map<string, number> {
  const now = Date.now();
  return new Map(properties.map((p) => [p.id, sortScore(p, now)]));
}

function sortValue(field: SortField, p: Property): number | null {
  return isNumericField(field)
    ? numericValue(field, p)
    : dateValue(field, p);
}

// valueOf: optional precomputed sort values by id (sortProperties builds it so
// each row's date is parsed once, not once per comparison).
export function makePropertyComparator(
  field: SortField,
  reversed: boolean,
  dealScoreOf: Map<string, number> | null,
  valueOf?: Map<string, number | null>
): (a: Property, b: Property) => number {
  if (field === "score") {
    // Fixed direction: highest score first. Not reversible.
    return (a, b) => (dealScoreOf?.get(b.id) ?? -1) - (dealScoreOf?.get(a.id) ?? -1);
  }
  const numeric = isNumericField(field);
  return (a, b) => {
    const av = valueOf ? valueOf.get(a.id) ?? null : sortValue(field, a);
    const bv = valueOf ? valueOf.get(b.id) ?? null : sortValue(field, b);
    if (av === null && bv === null) return 0;
    if (av === null) return 1; // missing → bottom, both directions
    if (bv === null) return -1;
    // Primary (non-reversed) direction: numeric = high→low; auction = soonest
    // (earliest date first); date_added = newest (latest date first).
    const primary = numeric
      ? LOW_FIRST_FIELDS.includes(field)
        ? av - bv
        : bv - av
      : field === "auction"
        ? av - bv
        : bv - av;
    return reversed ? -primary : primary;
  };
}

export function sortProperties(properties: Property[], field: SortField, reversed: boolean): Property[] {
  if (field === "score") {
    return [...properties].sort(makePropertyComparator(field, reversed, scoreMap(properties)));
  }
  const valueOf = new Map(properties.map((p) => [p.id, sortValue(field, p)]));
  return [...properties].sort(makePropertyComparator(field, reversed, null, valueOf));
}
